import React, {
  SyntheticEvent,
  Fragment,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { Box } from '@mui/material';
import { Button } from 'components/Buttons';
import MBImage from 'components/components/MBImage';
import { CustomIcon } from 'components/CustomIcon';
import MBCheckbox from 'components/Elements/MBCheckbox';
import TypographyComponent from 'components/Typography/Typography';
import { pxToEm } from 'theme';
import palette from 'theme/palette';
import * as Styles from './ProductCard.styles';
import {
  ColorVariant,
  ProductCardProps,
  ProjectDropdownVariant,
} from './ProductCard.types';
import ProjectDropdown from './ProjectDropdown';
import { DigitalSampleIcon } from '../../../../../../../../../../UILibrary';

const TopProductInfo: React.FC<Pick<ProductCardProps, 'topInfoItems'>> = ({
  topInfoItems,
}) => (
  <Styles.ProductInfoContainer top>
    <Styles.ProductInfoText variant="body" component="div" isTopLabel>
      {topInfoItems?.map(({ label, iconName, bgColor, textColor }) => (
        <Styles.FeatureItem
          key={String(label)}
          bgColor={bgColor}
          textColor={textColor}
          isTopLabel
        >
          <p>{label}</p>
          {iconName != null && <CustomIcon size={0.9} dense name={iconName} />}
        </Styles.FeatureItem>
      ))}
    </Styles.ProductInfoText>
  </Styles.ProductInfoContainer>
);

export const FavoritesButtons: React.FC<{
  showAddToFavorites: boolean;
  isFavoritesCallback?: boolean;
  shouldAlwaysShowFavoritesButton: boolean;
  onClickAddToFavorites?: () => void | undefined;
  isAddToFavoritesLoading: boolean;
  right?: number;
  entityId?: string | number;
  isMobileDevice?: boolean;
  saveButtonLabel?: string;
}> = ({
  showAddToFavorites,
  shouldAlwaysShowFavoritesButton,
  onClickAddToFavorites,
  isAddToFavoritesLoading,
  isMobileDevice = false,
  right = 16,
  entityId = undefined,
  saveButtonLabel = 'Save',
}) => {
  const isFavoritesShown =
    showAddToFavorites ||
    shouldAlwaysShowFavoritesButton ||
    isAddToFavoritesLoading;
  const iconName = 'wishlist';
  const btnId =
    entityId != null ? `search_item_${entityId}_boards_button` : undefined;

  if (!isFavoritesShown) return null;

  return (
    <Styles.TopRightButtonsWrapper
      top={16}
      right={right}
      isMobile={isMobileDevice}
      className="card-boards-button-wrapper"
    >
      {isMobileDevice ? (
        <CustomIcon
          id={btnId}
          name={iconName}
          size={1}
          dense
          onClick={onClickAddToFavorites}
        />
      ) : (
        <Button
          id={btnId}
          onClick={onClickAddToFavorites}
          iconDense
          label={saveButtonLabel}
          size="medium"
          sx={{
            fontSize: '13px',
          }}
          color="secondary"
          variant="contained"
        />
      )}
    </Styles.TopRightButtonsWrapper>
  );
};

const ProductCard = ({
  imageUrl,
  features = [],
  title,
  blurhash,
  topInfoItems = [],
  onProductClick,
  onRightClick,
  onClickAddToFavorites,
  onActionButtonClick: propOnActionButtonClick,
  isSelected,
  onSelected,
  onAddToBag,
  onProjectClick,
  onCreateProject,
  imageDecodeMode = 'eager',
  aspectRatio = 'original',
  showSelect = false,
  showSelectOnHover = false,
  showHoverFeatures = false,
  showFeatures = false,
  isAddToCartLoading = false,
  disabled = false,
  isAddedToCart = false,
  isAddToFavoritesLoading = false,
  shouldAlwaysShowFavoritesButton = false,
  isOvernight = false,
  showBag = true,
  selectedProject = null,
  projects = [],
  actionsButtons,
  entityId = undefined,
  productUrl,
  childrenNumber,
  isHoverAllowed = true,
  isOutlined = false,
  showProjectsDropdown = true,
  isMobileDevice = false,
  isSmallDesktop = false,
  showAdvancedInfo = false,
  centerAdvancedInfo = true,
  subtitle1,
  subtitle2,
  categoryName,
  brandName,
  actionType = 'addToCart',
  actionButtonText,
  hideActionButton,
  addToCartLabel = 'Add to Cart',
  addedToCartLabel = 'Added to Cart',
  createProjectButtonLabel = 'Create Project',
  inStockLabel = 'Ships overnight',
  outOfStockLabel = '1-5+ Days From Brand',
  projectPreLabel = 'Project: ',
  saveButtonLabel = 'Add to Board',
  isCompactDescription,
  showOnHoverOnlyOvernight = false,
  searchPlaceholderLabel = 'Search',
  digitalCatalogLabel = 'Digital Catalog',
  isDigital = false,
  withColorVariants = false,
  colorVariantsNumber = 0,
  colorVariants = [],
  onVariantClick,
  tooltipTitle,
  activeVariantId,
  isSelectboxDisabled = false,
  checkboxTooltipTitle = undefined,
  shouldHideFavoritesButton = false,
  customTopRightButtonCallback,
}: ProductCardProps) => {
  const [isCardHover, setIsHover] = useState(false);
  const [shouldShowContainedDropdown, setShouldShowContainedDropdown] =
    useState(false);
  const isHover = isCardHover && isHoverAllowed;
  const shouldShowEllipsis =
    actionsButtons != null && ((isHover && !isMobileDevice) || isMobileDevice);
  const hasProjects = projects.length > 0;

  const handleMouseMove = () => {
    if (!isHover) {
      setIsHover(true);
    }
  };

  const handleSelectClicked: React.MouseEventHandler<HTMLButtonElement> = (
    e,
  ) => {
    e.stopPropagation();
    onSelected?.(!isSelected);
  };

  const handleCoverImageClick = (e: SyntheticEvent) => {
    e.preventDefault();
    if (showSelect) {
      onSelected?.();
    } else {
      onProductClick?.();
    }
  };

  const handleProjectClick = (id: number) => {
    onProjectClick?.(id);
    setShouldShowContainedDropdown(false);
    setIsHover(false);
  };

  const handleRightClick = () => {
    onRightClick?.();
  };

  const shouldShowDetailedInfo = showAdvancedInfo;
  const isFavoritesCallback =
    customTopRightButtonCallback != null || onClickAddToFavorites != null;
  const showAddToFavorites =
    ((isFavoritesCallback && isHover) ||
      (!shouldShowDetailedInfo && shouldShowContainedDropdown)) &&
    !showSelect;
  const shouldShowSelect =
    showSelect || isSelected || (showSelectOnHover && isHover);
  const shouldShowFeatures =
    (showHoverFeatures && !showSelect && isHover) || showFeatures;

  const shouldShowBag = showBag && !showSelect;
  const showInfoDropdown =
    hasProjects &&
    selectedProject != null &&
    shouldShowBag &&
    !isAddedToCart &&
    showProjectsDropdown;
  const showTopProductInfo = !isSelected && !showSelect && !isHover;

  const isCustomAction = actionType === 'customAction';

  const shouldShowHoverAdvancedInfo =
    showAdvancedInfo &&
    (actionType === 'addToCart' || isCustomAction) &&
    showProjectsDropdown &&
    hasProjects &&
    selectedProject != null &&
    shouldShowBag &&
    isHover &&
    !isAddedToCart;

  const actionButtonLabel = useMemo(() => {
    if (actionType === 'link' || isCustomAction) {
      return actionButtonText;
    } else if (isAddedToCart) {
      return addedToCartLabel;
    } else {
      return addToCartLabel;
    }
  }, [
    actionType,
    isCustomAction,
    isAddedToCart,
    actionButtonText,
    addedToCartLabel,
    addToCartLabel,
  ]);

  const onActionButtonClick = useCallback(() => {
    if (isAddToCartLoading) {
      return;
    }

    if (actionType === 'addToCart' && !isAddedToCart) {
      onAddToBag?.(selectedProject?.id);
    }

    if (isCustomAction) {
      propOnActionButtonClick?.();
    }
  }, [
    isAddToCartLoading,
    actionType,
    isAddedToCart,
    isCustomAction,
    onAddToBag,
    selectedProject?.id,
    propOnActionButtonClick,
  ]);

  const variantsToShow = useMemo(() => {
    if (!colorVariants) return [];
    const smallScreenLimit = isSmallDesktop ? 4 : 6;
    return (colorVariants || []).slice(0, smallScreenLimit);
  }, [colorVariants, isSmallDesktop]);

  const shouldDisplayFeature = (feature: string, features: string[]) => {
    const isSpecialFeature = /^[$]+|Residential|Commercial$/i.test(feature);
    const hasManyFeatures = features.length > 3;
    return !hasManyFeatures || isSpecialFeature;
  };

  const BottomProductInfo: React.FC<{
    handleBottomInfoClick: (e: SyntheticEvent) => void;
  }> = ({ handleBottomInfoClick }) => (
    <Styles.ProductInfoContainer
      activeScroll
      isHover={isHover}
      showContainedInfo={shouldShowDetailedInfo || !showInfoDropdown}
      onClick={handleBottomInfoClick}
    >
      <Styles.ProductInfoText variant="body" component="div">
        {features?.map((feature) => {
          if (!shouldDisplayFeature(feature, features)) {
            return null;
          }
          return (
            <Styles.FeatureItem key={String(feature)}>
              <small>{feature}</small>
              <br />
            </Styles.FeatureItem>
          );
        })}
      </Styles.ProductInfoText>
    </Styles.ProductInfoContainer>
  );

  return (
    <Styles.RelativeContainer
      disabled={disabled}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onMouseMove={handleMouseMove}
    >
      <Styles.TooltipCursor
        followCursor
        PopperProps={{ disablePortal: true }}
        title={tooltipTitle}
      >
        <Styles.Container
          disabled={disabled}
          showSelect={showSelect}
          isAddToCartLoading={isAddToCartLoading}
        >
          {shouldShowSelect && (
            <Styles.CheckboxTooltip
              PopperProps={{ disablePortal: true }}
              title={checkboxTooltipTitle}
            >
              <Styles.SelectedIconContainer showAdvancedInfo={showAdvancedInfo}>
                <MBCheckbox
                  size={26}
                  onClick={handleSelectClicked}
                  checked={isSelected}
                  disabled={isSelectboxDisabled}
                />
              </Styles.SelectedIconContainer>
            </Styles.CheckboxTooltip>
          )}
          {shouldShowFeatures && (
            <BottomProductInfo handleBottomInfoClick={handleCoverImageClick} />
          )}
          <Styles.CoverContainer
            isMobile={isMobileDevice}
            isHover={isHover}
            isOutlined={isOutlined}
            onContextMenu={handleRightClick}
          >
            {shouldShowEllipsis && actionsButtons}
            {showTopProductInfo && (
              <Styles.TopWrapper>
                <TopProductInfo topInfoItems={topInfoItems} />
              </Styles.TopWrapper>
            )}
            {!shouldHideFavoritesButton && (
              <FavoritesButtons
                entityId={entityId}
                isFavoritesCallback={isFavoritesCallback}
                onClickAddToFavorites={
                  customTopRightButtonCallback != null
                    ? customTopRightButtonCallback
                    : onClickAddToFavorites
                }
                shouldAlwaysShowFavoritesButton={
                  shouldAlwaysShowFavoritesButton
                }
                showAddToFavorites={showAddToFavorites}
                isAddToFavoritesLoading={isAddToFavoritesLoading}
                right={shouldShowEllipsis ? 48 : 16}
                isMobileDevice={isMobileDevice}
                saveButtonLabel={saveButtonLabel}
              />
            )}

            <MBImage
              url={productUrl}
              imageUrl={imageUrl}
              onClick={handleCoverImageClick}
              aspectRatio={aspectRatio}
              blurHash={blurhash}
              imageDecodeMode={imageDecodeMode}
              alt={title}
              bgColor={palette.grey.overlay}
            />
            {(isHover ||
              isSelected ||
              (!shouldShowDetailedInfo && shouldShowContainedDropdown)) && (
              <Styles.DimmedBackground />
            )}
          </Styles.CoverContainer>
        </Styles.Container>
      </Styles.TooltipCursor>
      {withColorVariants && (
        <Styles.VariantContainer
          isSelectActive={showSelect}
          position="relative"
          gap="4px"
          display="flex"
          my="8px"
          height="24px"
        >
          {variantsToShow?.length > 0 && (
            <Fragment>
              {variantsToShow.map((item: ColorVariant) => {
                const image = item.image_url?.split('?')?.[0];
                const smallImage = `${image}?width=30`;
                return (
                  <Styles.TooltipInfo
                    key={item.entity_id}
                    PopperProps={{
                      disablePortal: true,
                    }}
                    title={item.exact_color}
                  >
                    <Styles.VariantIcon
                      isActive={activeVariantId === item.entity_id}
                      onClick={() => onVariantClick?.(item)}
                    >
                      <img
                        src={smallImage}
                        alt={item.exact_color}
                        loading="lazy"
                        width="24"
                        height="24"
                      />
                    </Styles.VariantIcon>
                  </Styles.TooltipInfo>
                );
              })}
              {colorVariants?.length > 6 && (
                <Styles.VariantLeft
                  variant="body"
                  onClick={() => onProductClick?.(undefined, true)}
                >
                  +{colorVariantsNumber - variantsToShow?.length}
                </Styles.VariantLeft>
              )}
            </Fragment>
          )}
        </Styles.VariantContainer>
      )}
      <Box position="relative">
        {showAdvancedInfo && (
          <Box
            className="product-info-container"
            display="flex"
            flexDirection="column"
            justifyContent={centerAdvancedInfo ? 'center' : undefined}
            height={isCompactDescription ? 'auto' : '56px'}
            marginTop={pxToEm(8)}
            padding={`0 ${pxToEm(4)}`}
          >
            {!shouldShowHoverAdvancedInfo && (
              <Fragment>
                <TypographyComponent
                  className="product-category-name"
                  fontSize={pxToEm(10)}
                  lineHeight="16px"
                  fontWeight={600}
                  color={palette.grey['A400']}
                  textTransform="uppercase"
                  noWrap
                >
                  {subtitle1 || categoryName}
                </TypographyComponent>
                <TypographyComponent
                  className="product-title"
                  variant="body"
                  fontWeight={600}
                  fontSize={pxToEm(13)}
                  lineHeight="20px"
                  noWrap
                >
                  {title}
                </TypographyComponent>
                <TypographyComponent
                  variant="body"
                  display="flex"
                  alignItems="center"
                  color={palette.grey['A400']}
                  fontSize={pxToEm(12)}
                  lineHeight="20px"
                >
                  <TypographyComponent
                    component="span"
                    inherit
                    noWrap
                    textAlign={isCompactDescription ? 'center' : 'left'}
                  >
                    {subtitle2 || brandName}
                  </TypographyComponent>
                  {!!childrenNumber && (
                    <TypographyComponent component="span" inherit>
                      &nbsp;({childrenNumber})
                    </TypographyComponent>
                  )}
                </TypographyComponent>
              </Fragment>
            )}
            {shouldShowHoverAdvancedInfo && (
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box display="flex" alignItems="center">
                  {isDigital ? (
                    <DigitalSampleIcon />
                  ) : isOvernight ? (
                    <Styles.SubtextIcon name="overnight" size={0.66} dense />
                  ) : null}
                  <TypographyComponent
                    fontSize={pxToEm(10)}
                    lineHeight={showOnHoverOnlyOvernight ? '20px' : '16px'}
                    fontWeight={600}
                    color={
                      isOvernight || isDigital
                        ? palette.info.main
                        : palette.grey['A400']
                    }
                    textTransform="uppercase"
                    noWrap
                  >
                    {isDigital
                      ? digitalCatalogLabel
                      : isOvernight
                      ? inStockLabel
                      : outOfStockLabel}
                  </TypographyComponent>
                </Box>
                {!showOnHoverOnlyOvernight && (
                  <Styles.StaticProjectDropdownContainer>
                    <ProjectDropdown
                      entityId={entityId}
                      variant={ProjectDropdownVariant.GHOST}
                      projects={projects}
                      selectedProject={selectedProject}
                      preLabel={projectPreLabel}
                      shouldShow
                      isContained={false}
                      hideAddToBag
                      onProjectClick={handleProjectClick}
                      onCreateProject={onCreateProject}
                      createProjectButtonLabel={createProjectButtonLabel}
                      searchPlaceholderLabel={searchPlaceholderLabel}
                    />
                  </Styles.StaticProjectDropdownContainer>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
      {showAdvancedInfo && showBag && (
        <Styles.ActionButtonContainer>
          <Styles.ActionButton
            id={
              entityId != null
                ? `search_item_${entityId}_action_button`
                : undefined
            }
            data-testid="add-to-cart"
            href={actionType === 'link' ? productUrl : undefined}
            variant="contained"
            color={
              isHover || isAddToCartLoading || isAddedToCart
                ? 'primary'
                : 'secondary'
            }
            startIcon={
              actionType === 'addToCart' && isAddedToCart ? 'check' : undefined
            }
            isStartIconCircularProgress={isAddToCartLoading}
            fullWidth
            disabled={disabled}
            hidden={hideActionButton || showSelect}
            onClick={onActionButtonClick}
          >
            <span className="buttonLabel">{actionButtonLabel}</span>
          </Styles.ActionButton>
        </Styles.ActionButtonContainer>
      )}
    </Styles.RelativeContainer>
  );
};

export default ProductCard;
